<template>
  <v-dialog
    class="setup-payment-dialog"
    fullscreen
    :model-value="isOpen"
    @update:model-value="
      (e) => {
        isOpen = e
        emit('update:model-value', e)
      }
    "
  >
    <template v-slot:activator="{ props: activatorProps }">
      <slot :props="activatorProps" name="activator"></slot>
    </template>

    <section class="setup-payment-container">
      <div class="wrapper">
        <button
          class="close-button"
          variant="plain"
          @click="
            () => {
              isOpen = false
              emit('update:model-value', false)
            }
          "
        ></button>
        <div class="text-content">
          <h2 class="header-title">
            {{ t('setup_payment_header') }}
          </h2>
          <div class="tutorial">
            <h4>{{ t('youre_so_close') }}</h4>
            <p v-html="t('add_valid_payment_tutorial_body')" />
            <p v-html="t('no_messy_billing')" />
            <p v-html="t('follow_the_link')" />
          </div>
          <h4>{{ t('setup_payment_note', { count: daysLeft }) }}</h4>
        </div>
        <gs-button
          :disabled="!goToCustomSpendingLimit"
          class="cta"
          type="primary"
          size="large"
          full-width
          @click="goToCustomSpendingLimit"
        >
          {{ t('manage_payment_cta') }}
        </gs-button>
      </div>
    </section>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const router = useRouter()

const emit = defineEmits<{ (e: 'update:model-value', value: boolean) }>()
const { startOpen, daysLeft } = withDefaults(
  defineProps<{
    daysLeft: number
    startOpen?: boolean
  }>(),
  { startOpen: false },
)

const isOpen = ref<boolean>(startOpen)

const goToCustomSpendingLimit = (): void => {
  router.push({ name: 'ShopifyCustomSpendingLimit' })
  isOpen.value = false
}
</script>

<style lang="scss" scoped>
@import '~vuetify/settings';

.setup-payment-dialog {
  background-color: var(--gray-background);
}

.setup-payment-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    padding: 32px 32px 0;
  }
}

.wrapper {
  position: relative;
  background-color: white;
  display: flex;
  width: 900px;
  min-height: 470px;
  padding: 21px 24px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
  border-radius: 24px;

  @media #{map-get($display-breakpoints, 'md-and-up')} {
    padding: 44px 48px;
    min-height: 543px;
  }

  .close-button {
    position: absolute;
    z-index: 1;
    top: 21px;
    right: 16px;
    width: 25px;
    height: 25px;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      top: 44px;
      right: 32px;
      width: 30px;
      height: 30px;
    }
  }

  .cta.gs-button.normal {
    height: auto;
  }

  p,
  h2,
  h4 {
    margin-bottom: 0;
  }

  .text-content {
    margin-bottom: 32px;
    flex-grow: 1;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    p,
    h4 {
      font-size: 16px;
      line-height: normal;

      @media #{map-get($display-breakpoints, 'md-and-up')} {
        font-size: 24px;
      }
    }
  }

  .header-title {
    font-size: 21px;
    line-height: normal;

    @media #{map-get($display-breakpoints, 'md-and-up')} {
      font-size: 32px;
    }
  }

  .tutorial {
    flex-grow: 1;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 16px;
  }
}
</style>
